import type { MediaManagerTemplate } from 'modules/manager/domain/interfaces';
import { TemplateType } from 'modules/manager/domain/template-type';
import type { RenderTemplateArgs } from 'modules/manager/store/render-template-api';

/**
 * Собирает параметры серверного рендера (`POST /api/render-template`) для шаблона,
 * открытого в редакторе, подставляя фикстурные id. Питает локальную кнопку «Рендер»
 * в шапке — в продукте рендер пока ниоткуда не запускается человеком, и кнопка
 * существует ровно для того, чтобы разработчик не собирал тело запроса руками.
 *
 * Id ниже — строки TEST-базы, захардкожены сознательно: кнопка закрыта `useIsLocal`
 * (см. editor-header.tsx), а это компайл-тайм-константа в любом собранном бандле, так
 * что на задеплоенном окружении кнопка не нарисуется ни у кого. Считайте id одноразовыми
 * фикстурами — здесь нет ни одного контракта, перенацеливайте свободно.
 */

/**
 * Самые «полные» данные, найденные на TEST-стенде (10.09.2026): соревнование 757
 * «Чемпионат Бердска» — единственное из 1837 с непустым `images`, его матчи 42380+
 * и этап 5, эмблемы у обеих команд есть. Даже на них рендер отдаёт 500 — см. ловушку №4
 * в `.claude/docs/media-manager-server-render.md`.
 */
const FIXTURE_STAGE_ID = 1430;
const FIXTURE_CHAMPIONSHIP_ID = 1450;
const FIXTURE_MATCH_IDS = [53398, 53654, 53995];

/**
 * Позиционно по регионам layout-блоков — `backgrounds[i]` заполняет регион `i`, лишние
 * сверх числа регионов шаблона сервис рендера игнорирует. Трёх хватает на любой пресет.
 *
 * Адреса здесь — только источник байтов: в поле `backgrounds` уезжает **файл**, а не
 * ссылка (см. `downloadFixtureBackgrounds`). Поэтому чужой хост обязан отдавать
 * CORS-заголовки — без них браузер не даст прочитать тело, и фона не будет.
 */
const FIXTURE_BG_URLS = [
  // `w=1080` под разрешение шаблона, а не оригинальные 3000px: в base64 картинка
  // прибавляет треть, и три фона в полный размер уже упираются в лимиты тела запроса.
  'https://images.unsplash.com/photo-1551958219-acbc608c6377?fm=jpg&q=60&w=1080&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1517649763962-0c623066013b?fm=jpg&q=60&w=1080&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?fm=jpg&q=60&w=1080&auto=format&fit=crop',
];

/**
 * По одному сборщику на тип — `Record<TemplateType, …>`, чтобы новый тип шаблона не
 * скомпилировался, пока для него не описан режим контекста рендера.
 *
 * Режим ровно один на запрос: на бэкенде ветки сбора контекста идут подряд без `else`,
 * и лишний id молча меняет выбранный режим (см. `buildRenderTemplateFormData`).
 */
const PARAM_BUILDERS: Record<TemplateType, (template: MediaManagerTemplate) => RenderTemplateArgs> = {
  [TemplateType.SCHEDULE]: (template) => ({ ...common(template), matchId: FIXTURE_MATCH_IDS[0] }),
  [TemplateType.RESULTS]: (template) => ({ ...common(template), matchId: FIXTURE_MATCH_IDS[0] }),
  [TemplateType.MULTIPLE_SCHEDULE]: (template) => ({ ...common(template), matchIds: [...FIXTURE_MATCH_IDS] }),
  [TemplateType.MULTIPLE_RESULTS]: (template) => ({ ...common(template), matchIds: [...FIXTURE_MATCH_IDS] }),
  [TemplateType.TOURNAMENT_TABLE]: (template) => ({ ...common(template), stageId: FIXTURE_STAGE_ID }),
  [TemplateType.TOP_PLAYERS_BY_INDICATORS]: (template) => ({ ...common(template), championshipId: FIXTURE_CHAMPIONSHIP_ID }),
};

/** Общая часть любого режима: сам шаблон и его подтипы. Фоны добавляются отдельно — они асинхронные. */
function common(template: MediaManagerTemplate): RenderTemplateArgs {
  return {
    templateId: template.id,
    templateSubtypes: [...template.templateSubtypes],
  };
}

/**
 * Скачивает фикстурные фоны и отдаёт их блобами — в поле `backgrounds` уезжают файлы.
 *
 * Недоступный фон просто выпадает из списка: контракт бэкенда — массив файлов, и
 * «пустого» файла в нём не выразить. Ошибку глушим: фон декоративен, картинка без него
 * осмысленна (ровно так же на неё смотрит и сервис рендера).
 */
async function downloadFixtureBackgrounds(signal?: AbortSignal): Promise<Blob[]> {
  const downloaded = await Promise.all(
    FIXTURE_BG_URLS.map(async (url) => {
      try {
        const response = await fetch(url, { signal });

        if (!response.ok) {
          throw new Error(url + ' → HTTP ' + response.status);
        }

        return await response.blob();
      } catch (error) {
        console.warn('[dev-render] фон не скачался, регион останется пустым:', url, error);
        return null;
      }
    }),
  );

  return downloaded.filter((blob): blob is Blob => blob !== null);
}

export async function buildDevRenderParams(
  template: MediaManagerTemplate,
  signal?: AbortSignal,
): Promise<RenderTemplateArgs> {
  const args = PARAM_BUILDERS[template.templateType](template);

  return { ...args, backgrounds: await downloadFixtureBackgrounds(signal) };
}
